import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import Loader from './components/Loader';
import { AuthContext } from './AuthProvider';
import ConfirmModal from './components/ConfirmModal';

export default function ManageJobRoles() {
  const { user } = useContext(AuthContext);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newRole, setNewRole] = useState('');
  const [editModal, setEditModal] = useState(false);
  const [editRole, setEditRole] = useState(null);
  const [editFields, setEditFields] = useState({ name: '', alternate_roles: '', keywords: '', indeed_keywords: '', jobTitlesToApplyFor: '', status: '' });
  const [statusFilter, setStatusFilter] = useState('All');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddConfirm, setShowAddConfirm] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [showAddCard, setShowAddCard] = useState(false);

  const filteredRoles = roles.filter(role => {
    if (statusFilter !== 'All') {
      const roleStatus = role.status || 'Active';
      if (roleStatus.toLowerCase() !== statusFilter.toLowerCase()) return false;
    }
    if (searchQuery.trim() !== '') {
      const roleName = role.name || '';
      if (!roleName.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    }
    return true;
  });

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = () => {
    setLoading(true);
    axios.get('/job-roles/')
      .then(res => setRoles(res.data))
      .catch(error => {
        console.error('Error fetching roles:', error);
        setRoles([]);
      })
      .finally(() => setLoading(false));
  };

  const handleAddRole = () => {
    if (!newRole || newRole.trim() === '') {
      alert('Please enter a job role name');
      return;
    }
    setShowAddConfirm(true);
  };

  const executeAddRole = () => {
    setIsAdding(true);
    axios.post('/add-job-role/', { name: newRole.trim() }, {
      headers: {
        'Content-Type': 'application/json',
      }
    }).then((response) => {
      setNewRole('');
      fetchRoles();
      setShowAddConfirm(false);
      setShowAddCard(false);
    }).catch(error => {
      console.error('Error adding role:', error);
      alert('Error adding job role: ' + (error.response?.data?.error || error.message));
    })
      .finally(() => setIsAdding(false));
  };

  const openEditModal = async (role) => {
    try {
      // Fetch detailed role data including keywords and job titles
      const response = await axios.get(`/job-role-details/?id=${role.id}`);
      const roleDetails = response.data;

      setEditRole(role);
      const fields = {
        name: roleDetails.name || role.name,
        alternate_roles: roleDetails.alternate_roles || '',
        keywords: roleDetails.keywords || '',
        indeed_keywords: roleDetails.indeed_keywords || '',
        jobTitlesToApplyFor: roleDetails.job_titles || '',
        status: roleDetails.status || 'Active'
      };
      setEditFields(fields);
      setEditModal(true);
    } catch (error) {
      console.error('Error fetching role details:', error);
      alert('Error loading role details');
    }
  };

  const closeEditModal = () => {
    setEditModal(false);
    setEditRole(null);
  };

  const saveEdit = () => {
    setLoading(true);
    const payload = {
      id: editRole.id,
      name: editFields.name,
      alternate_roles: editFields.alternate_roles,
      keywords: editFields.keywords,
      indeed_keywords: editFields.indeed_keywords,
      job_titles: editFields.jobTitlesToApplyFor,
      status: editFields.status
    };

    axios.post('/edit-job-role/', payload, {
      headers: {
        'Content-Type': 'application/json',
      }
    }).then((response) => {
      closeEditModal();
      fetchRoles();
    }).catch(error => {
      console.error('Error saving role:', error);
      alert('Error saving job role: ' + (error.response?.data?.error || error.message));
    })
      .finally(() => setLoading(false));
  };

  const handleDeleteRole = (role) => {
    setIsDeleting(true);
    const formData = new FormData();
    formData.append('id', role.id);

    axios.post('/delete-job-role/', formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      }
    }).then(() => {
      fetchRoles();
      setDeleteTarget(null);
    }).catch(error => {
      console.error('Error deleting role:', error);
      alert('Error deleting job role');
    }).finally(() => {
      setIsDeleting(false);
    });
  };

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-8 md:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header Block */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white p-6 rounded-xl border border-slate-200/80 shadow-sm mb-6">
          <div>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-50 text-indigo-600 rounded-lg">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-slate-800">Job Roles Management</h1>
            </div>
            <p className="text-sm text-slate-500 mt-1 pl-1">
              Configure job titles, alternative aliases, keywords, and system mapping parameters
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button 
              className="flex items-center gap-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 transition-colors px-4 py-2 rounded-lg shadow-sm"
              onClick={() => setShowAddCard(!showAddCard)}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
              </svg>
              {showAddCard ? "Hide Add Form" : "Add Job Role"}
            </button>
            <button 
              className="flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-indigo-600 transition-colors bg-slate-50 hover:bg-indigo-50 border border-slate-200 hover:border-indigo-200 px-4 py-2 rounded-lg"
              onClick={() => window.location.href = '/manage-jobs'}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Job Listings
            </button>
          </div>
        </div>

        {/* Add Job Role Block */}
        {showAddCard && (
          <div className="bg-white p-6 rounded-xl border border-slate-200/80 shadow-sm mb-6 transition-all duration-250">
            <h2 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
              <span className="w-1 h-5 bg-indigo-600 rounded-full inline-block"></span>
              Add New Job Role
            </h2>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
              <input
                type="text"
                value={newRole}
                onChange={e => setNewRole(e.target.value)}
                onKeyPress={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddRole();
                  }
                }}
                className="border border-slate-200 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 px-4 py-2.5 rounded-lg flex-grow transition-all text-sm outline-none"
                placeholder="Enter job role name (e.g. Software Engineer)"
              />
              <button 
                className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium text-sm px-6 py-2.5 rounded-lg shadow-sm hover:shadow active:scale-95 transition-all duration-150 sm:w-auto w-full flex items-center justify-center gap-2"
                onClick={handleAddRole}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                </svg>
                Add Role
              </button>
            </div>
          </div>
        )}

        {/* Existing Job Roles Block */}
        <div className="bg-white rounded-xl border border-slate-200/80 shadow-sm mb-6 overflow-hidden">
          <div className="p-6 border-b border-slate-100 flex flex-col md:flex-row justify-between items-slate-stretch md:items-center gap-4">
            <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <span className="w-1 h-5 bg-indigo-600 rounded-full inline-block"></span>
              Existing Job Roles
            </h2>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <div className="relative">
                <input
                  id="search-roles"
                  type="text"
                  placeholder="Search job roles..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="pl-9 pr-4 py-2 border border-slate-200 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 rounded-lg text-sm w-full sm:w-60 transition-all outline-none"
                />
                <div className="absolute left-3 top-2.5 text-slate-400">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <select
                  id="status-filter"
                  value={statusFilter}
                  onChange={e => setStatusFilter(e.target.value)}
                  className="border border-slate-200 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 px-3 py-2 rounded-lg text-sm bg-white outline-none w-full sm:w-auto transition-all"
                >
                  <option value="All">All Statuses</option>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
            </div>
          </div>

          <div className="overflow-auto max-h-[500px]">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100 text-slate-600 text-xs font-semibold uppercase tracking-wider">
                  <th className="px-6 py-4">Job Role</th>
                  <th className="px-6 py-4">Alternate Roles</th>
                  <th className="px-6 py-4">Status</th>
                  <th className="px-6 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-slate-700 text-sm">
                {loading ? (
                  <tr>
                    <td colSpan="4" className="p-8 text-center">
                      <div className="flex justify-center items-center py-4">
                        <Loader />
                      </div>
                    </td>
                  </tr>
                ) : filteredRoles.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="p-8 text-center text-slate-400">
                      {roles.length === 0 ? 'No job roles available' : 'No job roles match the current search or filters'}
                    </td>
                  </tr>
                ) : (
                  filteredRoles.map(role => (
                    <tr key={role.id} className="hover:bg-slate-50/50 transition-colors">
                      <td className="px-6 py-4 font-medium text-slate-900">{role.name}</td>
                      <td className="px-6 py-4 text-slate-500 max-w-xs truncate">
                        {Array.isArray(role.alternate_roles) 
                          ? role.alternate_roles.join(', ') 
                          : (role.alternate_roles || <span className="text-slate-300 italic">None</span>)
                        }
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold transition-colors ${
                          role.status === 'Inactive' 
                            ? 'bg-red-50 text-red-700 border border-red-100' 
                            : 'bg-green-50 text-green-700 border border-green-100'
                        }`}>
                          <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                            role.status === 'Inactive' ? 'bg-red-500' : 'bg-green-500'
                          }`}></span>
                          {role.status || 'Active'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          <button 
                            className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-900 bg-indigo-50 hover:bg-indigo-100/80 px-2.5 py-1 rounded-md text-xs font-semibold transition-all"
                            onClick={() => openEditModal(role)}
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                            Edit
                          </button>
                          {user?.role_name !== 'Associate admin' && (
                            <button 
                              className="inline-flex items-center gap-1 text-red-600 hover:text-red-900 bg-red-50 hover:bg-red-100/80 px-2.5 py-1 rounded-md text-xs font-semibold transition-all"
                              onClick={() => setDeleteTarget(role)}
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {editModal && (
        <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-96">
            <h2 className="text-lg font-semibold mb-4">Edit Job Role</h2>
            <form>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700">Role Name</label>
                <input type="text" value={editFields.name} onChange={e => setEditFields(f => ({ ...f, name: e.target.value }))} className="border border-gray-300 px-4 py-2 rounded-md w-full" />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700">Alternate Roles (comma-separated)</label>
                <input type="text" value={editFields.alternate_roles} onChange={e => setEditFields(f => ({ ...f, alternate_roles: e.target.value }))} className="border border-gray-300 px-4 py-2 rounded-md w-full" />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700">Keywords (comma-separated)</label>
                <input
                  type="text"
                  value={editFields.keywords}
                  onChange={e => {
                    setEditFields(f => ({ ...f, keywords: e.target.value }));
                  }}
                  className="border border-gray-300 px-4 py-2 rounded-md w-full"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700">Indeed Keywords (comma-separated)</label>
                <input
                  type="text"
                  value={editFields.indeed_keywords}
                  onChange={e => {
                    setEditFields(f => ({ ...f, indeed_keywords: e.target.value }));
                  }}
                  className="border border-gray-300 px-4 py-2 rounded-md w-full"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700">Job Titles to Apply For</label>
                <textarea value={editFields.jobTitlesToApplyFor} onChange={e => setEditFields(f => ({ ...f, jobTitlesToApplyFor: e.target.value }))} className="border border-gray-300 px-4 py-2 rounded-md w-full" />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={editFields.status || 'Active'}
                  onChange={e => setEditFields(f => ({ ...f, status: e.target.value }))}
                  className="border border-gray-300 px-4 py-2 rounded-md w-full bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
              <div className="flex justify-end gap-4">
                <button type="button" className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md" onClick={closeEditModal}>Cancel</button>
                <button type="button" className="bg-purple-600 text-white px-4 py-2 rounded-md" onClick={saveEdit}>Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
      <ConfirmModal
        isOpen={!!deleteTarget}
        title="Delete Job Role"
        message={`Are you sure you want to delete the job role "${deleteTarget ? deleteTarget.name : ''}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        loading={isDeleting}
        onConfirm={() => handleDeleteRole(deleteTarget)}
        onCancel={() => setDeleteTarget(null)}
        danger={true}
      />
      <ConfirmModal
        isOpen={showAddConfirm}
        title="Add Job Role"
        message={`Are you sure you want to add the job role "${newRole}"?`}
        confirmText="Add"
        cancelText="Cancel"
        loading={isAdding}
        onConfirm={executeAddRole}
        onCancel={() => setShowAddConfirm(false)}
        danger={false}
      />
    </div>
  );
}
